import { useEffect, useState } from 'react';
import { ArrowLeft, Edit3, Search, Trash2 } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, fetchManufacturerById } from '../api';
import CustomDescription from '../components/CustomDescription';
import RichTextEditor from '../components/RichTextEditor';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const copy = {
  RU: { back: 'Производители', kind: 'ПРОИЗВОДИТЕЛЬ', loading: 'Загрузка записи…', error: 'Не удалось загрузить запись.', related: 'Найти связанные записи', description: 'Описание', source: 'Сайт / источник', edit: 'Редактировать', remove: 'Удалить', save: 'Сохранить', cancel: 'Отмена', placeholder: 'Введите описание производителя', saveError: 'Не удалось сохранить изменения.', confirm: 'Удалить производителя?', deleteError: 'Не удалось удалить производителя.' },
  EN: { back: 'Manufacturers', kind: 'MANUFACTURER', loading: 'Loading record…', error: 'Unable to load the record.', related: 'Find related records', description: 'Description', source: 'Website / source', edit: 'Edit', remove: 'Delete', save: 'Save', cancel: 'Cancel', placeholder: 'Enter manufacturer description', saveError: 'Unable to save changes.', confirm: 'Delete this manufacturer?', deleteError: 'Unable to delete the manufacturer.' },
};

function hasAdminRole(user) {
  const roles = [user?.role, ...(user?.roles || []), ...(user?.authorities || [])]
    .map((role) => typeof role === 'string' ? role : role?.authority || role?.name);
  return roles.includes('ADMIN') || roles.includes('ROLE_ADMIN');
}

export default function ManufacturerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { lang } = useLanguage();
  const labels = copy[lang] || copy.RU;
  const isAdmin = hasAdminRole(currentUser);
  const [manufacturer, setManufacturer] = useState(null);
  useDocumentTitle(manufacturer?.name);
  const [form, setForm] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    window.scrollTo(0, 0);
    fetchManufacturerById(id)
      .then(({ data }) => {
        if (!active) return;
        setManufacturer(data);
        setForm(data);
        setStatus('ready');
      })
      .catch(() => active && setStatus('error'));
    return () => { active = false; };
  }, [id]);

  const handleSave = async () => {
    try {
      const { data } = await api.put('/manufacturers/' + id, form);
      setManufacturer(data);
      setForm(data);
      setEditMode(false);
      setError('');
    } catch { setError(labels.saveError); }
  };

  const handleDelete = async () => {
    if (!window.confirm(labels.confirm)) return;
    try { await api.delete('/manufacturers/' + id); navigate('/manufacturers'); }
    catch { window.alert(labels.deleteError); }
  };

  if (status === 'loading') return <main className="record-state page-width">{labels.loading}</main>;
  if (status === 'error' || !manufacturer) return <main className="record-state page-width">{labels.error}</main>;

  return (
    <main className="manufacturer-page page-width">
      <header className="manufacturer-header">
        <Link to="/manufacturers" className="record-breadcrumb"><ArrowLeft size={14} />{labels.back}</Link>
        <p className="section-kicker">{labels.kind} {String(manufacturer.id).padStart(3, '0')}</p>
        {editMode
          ? <input className="manufacturer-title-input" value={form.name || ''} onChange={(event) => setForm((value) => ({ ...value, name: event.target.value }))} />
          : <h1>{manufacturer.name}</h1>}
        <div className="manufacturer-actions">
          <Link to={'/search?searchType=all&manufacturer=' + encodeURIComponent(manufacturer.name)} className="secondary-button"><Search size={16} />{labels.related}</Link>
          {isAdmin && !editMode && <button type="button" className="secondary-button" onClick={() => setEditMode(true)}><Edit3 size={16} />{labels.edit}</button>}
          {isAdmin && !editMode && <button type="button" className="text-button danger-action" onClick={handleDelete}><Trash2 size={15} />{labels.remove}</button>}
        </div>
      </header>

      {error && <p className="manufacturer-error">{error}</p>}
      {editMode ? (
        <section className="manufacturer-editor">
          <label><span>{labels.description}</span><RichTextEditor value={form.description || ''} onChange={(value) => setForm((current) => ({ ...current, description: value }))} placeholder={labels.placeholder} /></label>
          <label><span>{labels.source}</span><input value={form.url || ''} onChange={(event) => setForm((current) => ({ ...current, url: event.target.value }))} /></label>
          <div><button type="button" className="primary-button" onClick={handleSave}>{labels.save}</button><button type="button" className="secondary-button" onClick={() => { setEditMode(false); setForm(manufacturer); setError(''); }}>{labels.cancel}</button></div>
        </section>
      ) : (
        <div className="manufacturer-body">
          {manufacturer.description && <section><h2>{labels.description}</h2><CustomDescription html={manufacturer.description} /></section>}
          {manufacturer.url && <aside><span>{labels.source}</span><a href={manufacturer.url} target="_blank" rel="noopener noreferrer">{manufacturer.url}</a></aside>}
        </div>
      )}
    </main>
  );
}
